import type {Extension, CreateExtensionArgument, DmApis, ResourceLoaderDefinition} from '@wix/document-manager-core'
import type {
    ListFragmentsMetadataResponse,
    GetFragmentResponse,
    FragmentTypeAndName,
    FragmentMetadata,
    Fragment
} from '@wix/ambassador-document-management-fragments-v1-fragment-snapshot/types'
import {type Resource, RESOURCE_TYPES} from '../../constants/constants'
import type {DocumentServicesModelExtApi} from '../documentServicesModel'
import _ from 'lodash'
import type {FragmentsExtensionAPI} from '../fragments/fragments'
import {mapGetFragmentsDataResponse} from './fragmentLoaderUtils'

interface FragmentServer {
    listFragments(branchId?: string): Promise<ListFragmentsMetadataResponse>
    getFragmentData(fragmentTypeAndName: FragmentTypeAndName, branchId?: string): Promise<GetFragmentResponse>
    getFragmentsData(fragmentTypeAndNames: FragmentTypeAndName[], branchId?: string): Promise<GetFragmentResponse[]>
}

const createExtension = ({experimentInstance, environmentContext, dsConfig}: CreateExtensionArgument): Extension => {
    // When we have an index, we should get this list from the index and remove this line
    const tempIndexMock: Record<string, Map<string, FragmentMetadata>> = {}

    // This is meant to avoid calling listFragments multiple times (since it returns all fragment types)
    // /!\ this works only as long as initAvailableResources for each fragment type are called synchronously
    let hasListedFragmentsMetaData = false

    const listFragments = async (branchId?: string): Promise<ListFragmentsMetadataResponse> => {
        return await environmentContext.serverFacade.listFragments(branchId)
    }

    const getFragmentsData = async (
        fragmentTypeAndNames: FragmentTypeAndName[],
        branchId?: string
    ): Promise<GetFragmentResponse[]> => {
        return (await environmentContext.serverFacade.getFragmentsData(fragmentTypeAndNames, branchId)).map(
            mapGetFragmentsDataResponse
        )
    }

    const setFragmentsToDal = (dmAPIs: DmApis, fragments: Fragment[]) => {
        if (!fragments.length) {
            return
        }
        const {fragments: fragmentsExtension} = dmAPIs.extensionAPI as FragmentsExtensionAPI
        _.forEach(fragments, fragment => {
            fragmentsExtension.mergeFragmentIndex({
                name: fragment.name,
                type: fragment.type
            })
            fragmentsExtension.mergeFragment(fragment)
        })
    }

    const createResourceLoaderDefinition = (fragmentType: Resource, dmApis: DmApis): ResourceLoaderDefinition => {
        const load = async (resourcesToLoad: FragmentMetadata[]) => {
            if (!dsConfig.supportedFragments?.[fragmentType]) {
                throw new Error(`Fragment type ${fragmentType} is not supported by the configuration`)
            }
            const missingFragments = resourcesToLoad.filter(
                ({name}) => !name || !tempIndexMock[fragmentType]?.has(name)
            )
            if (missingFragments.length) {
                throw new Error(
                    `Fragments ${missingFragments
                        .map(fragment => `(${fragment.type},${fragment.name})`)
                        .join(', ')} are not available in fragmentDefinitions.${fragmentType}`
                )
            }
            const {siteAPI} = dmApis.extensionAPI as DocumentServicesModelExtApi
            const fragmentsRes = await getFragmentsData(resourcesToLoad, siteAPI.getBranchId())
            const fragmentsData = (fragmentsRes?.map(res => res.fragment)?.filter(Boolean) as FragmentMetadata[]) ?? []
            setFragmentsToDal(dmApis, fragmentsData)
        }

        const loadAll = async () => {
            if (!tempIndexMock[fragmentType]?.size) {
                console.warn(`no fragments of type ${fragmentType} available`)
                return
            }
            return load(Array.from(tempIndexMock[fragmentType].values()))
        }

        const getAvailableResources = () => {
            return Array.from(tempIndexMock[fragmentType]?.keys() ?? [])
        }

        const initAvailableResources = async () => {
            if (!experimentInstance.isOpen('dm_fragmentsLoadOnInit') || dsConfig.disableFragments) {
                return
            }
            const fragmentSingletonName = dsConfig.supportedFragments?.[fragmentType]?.singletonInstance
            async function loadAndSetMetadata() {
                const {siteAPI} = dmApis.extensionAPI as DocumentServicesModelExtApi
                const {fragments} = await listFragments(siteAPI.getBranchId())
                fragments?.forEach(fragment => {
                    if (!fragment.type || !fragment.name) {
                        return
                    }
                    if (!dsConfig.supportedFragments?.[fragment.type]) {
                        return
                    }
                    if (!tempIndexMock[fragment.type]) {
                        tempIndexMock[fragment.type] = new Map()
                    }
                    tempIndexMock[fragment.type].set(fragment.name, fragment)
                })
                hasListedFragmentsMetaData = true
            }
            if (dsConfig.loadFragmentsMetaData && !hasListedFragmentsMetaData) {
                await loadAndSetMetadata()
            } else if (!dsConfig.loadFragmentsMetaData && fragmentSingletonName) {
                const fragment = {
                    name: fragmentSingletonName,
                    type: fragmentType
                }
                if (!tempIndexMock[fragment.type]) {
                    tempIndexMock[fragment.type] = new Map()
                }
                tempIndexMock[fragment.type].set(fragment.name, fragment)
            } else if (
                !dsConfig.loadFragmentsMetaData &&
                dsConfig.supportedFragments?.[fragmentType]?.loadOnInit &&
                !hasListedFragmentsMetaData
            ) {
                await loadAndSetMetadata()
            }

            if (dsConfig.supportedFragments?.[fragmentType]?.loadOnInit) {
                await loadAll()
            }
        }

        return {
            resourceType: fragmentType,
            load: (fragments: any[]) => load(fragments),
            loadAll,
            getAvailableResources,
            initAvailableResources
        }
    }

    const createResourceLoader = (dmAPIs: DmApis): ResourceLoaderDefinition[] => {
        //TODO-fragments: Automatically create resource loaders based on dm config (supported fragments)
        const resourceLoaderDefinitions = [createResourceLoaderDefinition(RESOURCE_TYPES.APP_STATE, dmAPIs)]
        if (dmAPIs.coreConfig.experimentInstance.isOpen('dm_fragmentsEditorFeatures')) {
            resourceLoaderDefinitions.push(createResourceLoaderDefinition(RESOURCE_TYPES.EDITOR_FEATURES, dmAPIs))
        }
        return resourceLoaderDefinitions
    }

    return {
        name: 'fragmentResourceLoader',
        createResourceLoader
    }
}

export {createExtension, FragmentServer}
